import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, KeyRound, Plus, RefreshCw, ShieldCheck, UserRound, X, Power } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';

export default function AdminTeam() {
  const { me } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [secret, setSecret] = useState(null);
  const [form, setForm] = useState({
    username: '', display_name: '', email: '', password: '', permissions: ['dashboard', 'providers'],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([
        api.get('/admin/access/accounts'),
        api.get('/admin/access/permissions'),
      ]);
      setAccounts(a.data.accounts || []);
      setPermissionOptions(p.data.permissions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unable to load team access');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePermission = (key) => {
    setForm((current) => {
      const has = current.permissions.includes(key);
      const next = has ? current.permissions.filter((p) => p !== key) : [...current.permissions, key];
      return { ...current, permissions: next.includes('dashboard') ? next : ['dashboard', ...next] };
    });
  };

  const createAccount = async () => {
    try {
      const { data } = await api.post('/admin/access/accounts', {
        ...form,
        email: form.email || undefined,
        password: form.password || undefined,
      });
      setSecret({
        title: 'Team member created',
        username: data.account.username,
        password: data.temporary_password,
        recovery: data.recovery_code,
      });
      setCreateOpen(false);
      setForm({ username: '', display_name: '', email: '', password: '', permissions: ['dashboard', 'providers'] });
      await load();
      toast.success('Access account created');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create account');
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/admin/access/accounts/${editing.id}`, {
        display_name: editing.display_name,
        email: editing.email || undefined,
        permissions: editing.permissions,
        active: editing.active,
      });
      setEditing(null);
      await load();
      toast.success('Permissions updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const resetPassword = async (account) => {
    try {
      const { data } = await api.post(`/admin/access/accounts/${account.id}/reset-password`, {});
      setSecret({
        title: 'New temporary credentials',
        username: account.username,
        password: data.temporary_password,
        recovery: data.recovery_code,
      });
      await load();
      toast.success('Credentials rotated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reset password');
    }
  };

  const activeCount = useMemo(() => accounts.filter((a) => a.active).length, [accounts]);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Clipboard access was blocked by the browser');
    }
  };

  if (!me?.is_owner) {
    return <div className="admin-page"><div className="admin-card"><ShieldCheck size={28} /><h1>Owner access required</h1><p>This area is restricted to the RESQLY main account.</p></div></div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-page-head">
        <div>
          <div className="admin-eyebrow"><KeyRound size={13} /> Access control</div>
          <h1>Team access</h1>
          <p>Create operator accounts and give each person only the parts of RESQLY they need.</p>
        </div>
        <button className="admin-lux-button" onClick={() => setCreateOpen(true)}><Plus size={18} /> Create account</button>
      </div>

      <div className="admin-kpi-strip">
        <div className="admin-stat-card"><span>Active team</span><strong>{activeCount}</strong><small>accounts currently enabled</small></div>
        <div className="admin-stat-card"><span>Permission model</span><strong>{permissionOptions.filter((p) => p.key !== 'team_access').length}</strong><small>separate operational scopes</small></div>
        <div className="admin-stat-card"><span>Owner</span><strong>1</strong><small>non-delegable main access</small></div>
      </div>

      <div className="admin-card">
        <div className="admin-card-head">
          <div><h2>Operator accounts</h2><p>Disable access instantly. Permission changes apply to the next API request.</p></div>
          <button className="admin-icon-button" onClick={load} aria-label="Refresh"><RefreshCw size={17} className={loading ? 'animate-spin' : ''} /></button>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Operator</th><th>Access</th><th>Status</th><th>Last login</th><th /></tr></thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td><div className="admin-person-cell"><div className="admin-table-avatar">{account.display_name?.slice(0,1)?.toUpperCase() || 'T'}</div><div><strong>{account.display_name}</strong><span>@{account.username}</span></div></div></td>
                  <td><div className="admin-permission-chips">{(account.permissions || []).slice(0, 4).map((p) => <span key={p}>{permissionOptions.find((x) => x.key === p)?.label || p}</span>)}{(account.permissions || []).length > 4 && <span>+{account.permissions.length - 4}</span>}</div></td>
                  <td><span className={`admin-status ${account.active ? 'success' : 'neutral'}`}>{account.active ? 'Active' : 'Disabled'}</span></td>
                  <td>{account.last_login_at ? new Date(account.last_login_at).toLocaleString() : 'Never'}</td>
                  <td><div className="admin-row-actions"><button onClick={() => setEditing({ ...account, permissions: account.permissions || [] })}>Edit</button><button onClick={() => resetPassword(account)} title="Rotate password"><RefreshCw size={15} /></button></div></td>
                </tr>
              ))}
              {accounts.length === 0 && !loading && <tr><td colSpan="5"><div className="admin-empty">No team accounts yet. Create the first operator account.</div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {createOpen && (
          <AdminModal title="Create operator account" onClose={() => setCreateOpen(false)}>
            <AccountForm form={form} setForm={setForm} permissionOptions={permissionOptions} togglePermission={togglePermission} submit={createAccount} submitLabel="Create account" />
          </AdminModal>
        )}
        {editing && (
          <AdminModal title={`Edit ${editing.display_name}`} onClose={() => setEditing(null)}>
            <div className="space-y-5">
              <div className="admin-form-grid">
                <label><span>Display name</span><input value={editing.display_name || ''} onChange={(e) => setEditing({ ...editing, display_name: e.target.value })} /></label>
                <label><span>Recovery email</span><input type="email" value={editing.email || ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></label>
              </div>
              <PermissionGrid options={permissionOptions} selected={editing.permissions || []} onToggle={(key) => setEditing({ ...editing, permissions: (editing.permissions || []).includes(key) ? editing.permissions.filter((p) => p !== key) : [...editing.permissions, key] })} />
              <div className="admin-danger-row"><div><strong>Account status</strong><p>Disabled accounts are rejected at the API, even with a valid old token.</p></div><button className={`admin-toggle ${editing.active ? 'on' : ''}`} onClick={() => setEditing({ ...editing, active: !editing.active })}><span /></button></div>
              <div className="admin-modal-actions"><button className="admin-quiet-button" onClick={() => setEditing(null)}>Cancel</button><button className="admin-lux-button" onClick={saveEdit}>Save changes</button></div>
            </div>
          </AdminModal>
        )}
        {secret && (
          <AdminModal title={secret.title} onClose={() => setSecret(null)} wide>
            <div className="admin-secret-card">
              <div><span>Username</span><strong>{secret.username}</strong><button onClick={() => copy(secret.username, 'Username')}><Copy size={14} /> Copy</button></div>
              <div><span>Temporary password</span><strong>{secret.password}</strong><button onClick={() => copy(secret.password, 'Password')}><Copy size={14} /> Copy</button></div>
              <div><span>Recovery code</span><strong>{secret.recovery}</strong><button onClick={() => copy(secret.recovery, 'Recovery code')}><Copy size={14} /> Copy</button></div>
            </div>
            <div className="admin-callout"><strong>Important:</strong> share these credentials through a secure channel. The operator must change the temporary password on first login.</div>
            <div className="admin-modal-actions"><button className="admin-lux-button" onClick={() => setSecret(null)}>Done</button></div>
          </AdminModal>
        )}
      </AnimatePresence>
    </div>
  );
}

function AccountForm({ form, setForm, permissionOptions, togglePermission, submit, submitLabel }) {
  return (
    <div className="space-y-5">
      <div className="admin-form-grid">
        <label><span>Username / ID</span><input placeholder="e.g. operations.jane" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></label>
        <label><span>Display name</span><input placeholder="Jane — Operations" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></label>
        <label><span>Recovery email <em>optional</em></span><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label><span>Temporary password <em>leave blank to generate</em></span><input type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
      </div>
      <PermissionGrid options={permissionOptions} selected={form.permissions} onToggle={togglePermission} />
      <div className="admin-callout">Only the modules selected above are granted. Team Access is reserved for the main account.</div>
      <div className="admin-modal-actions"><button className="admin-lux-button" onClick={submit}>{submitLabel}</button></div>
    </div>
  );
}

function PermissionGrid({ options, selected, onToggle }) {
  return (
    <div>
      <div className="admin-section-minihead"><strong>Module access</strong><span>Select only what this person should see and operate.</span></div>
      <div className="admin-permission-grid">
        {options.map((item) => {
          const checked = selected.includes(item.key);
          const disabled = item.key === 'team_access';
          return (
            <button key={item.key} type="button" disabled={disabled} onClick={() => onToggle(item.key)} className={`admin-permission-option ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`}>
              <span className="admin-check">{checked ? '✓' : ''}</span>
              <span><strong>{item.label}</strong><small>{permissionDescription(item.key)}</small></span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function permissionDescription(key) {
  return {
    dashboard: 'Operational overview and KPIs',
    providers: 'Provider onboarding, KYC and approvals',
    customers: 'Customer support and account operations',
    dispatch: 'Provider dispatch and alert configuration',
    services: 'Service availability and service settings',
    payments: 'Payment records and reconciliation',
    finance: 'Financial config and payout actions',
    referrals: 'Referral network and settings',
    pharmacy: 'Pharmacy workflows',
    labs: 'Lab workflows',
    audit: 'Read administrative security history',
    team_access: 'Owner only',
  }[key] || 'Administrative scope';
}

function AdminModal({ title, onClose, children, wide = false }) {
  return (
    <motion.div className="admin-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={onClose}>
      <motion.div className={`admin-modal ${wide ? 'wide' : ''}`} initial={{ opacity: 0, y: 22, scale: 0.985, filter: 'blur(10px)' }} animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }} exit={{ opacity: 0, y: 16 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="admin-modal-head"><div><div className="admin-eyebrow"><KeyRound size={13} /> Secure action</div><h2>{title}</h2></div><button className="admin-icon-button" onClick={onClose}><X size={17} /></button></div>
        {children}
      </motion.div>
    </motion.div>
  );
}
